import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { apiClient } from '../services/api';
import type { SupportTicket, SupportTicketMessage } from '../types';
import './SupportPage.css';

export const SupportTicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchTicket = async () => {
      if (!id) return;

      try {
        const data = await apiClient.getSupportTicket(parseInt(id, 10));
        setTicket(data);
      } catch (err) {
        console.error('Error fetching ticket:', err);
        setError('Ошибка загрузки обращения');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicket();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const formatMessageTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    if (diffDays === 0) {
      return time;
    } else if (diffDays === 1) {
      return `Вчера, ${time}`;
    } else {
      return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) + `, ${time}`;
    }
  };

  const handleSendMessage = async () => {
    if (!ticket || !replyText.trim() || isSending) return;

    setIsSending(true);
    try {
      const newMessage = await apiClient.sendSupportMessage(ticket.id, {
        message: replyText.trim(),
      });

      setTicket({
        ...ticket,
        messages: [...(ticket.messages || []), newMessage],
      });
      setReplyText('');
    } catch (err) {
      console.error('Error sending message:', err);
      // Could show a toast here
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="page-container support-page">
        <PageHeader title="Обращение" />
        <div className="support-loading">
          <div className="support-loading-spinner"></div>
          <p className="support-loading-text">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="page-container support-page">
        <PageHeader title="Обращение" />
        <div className="tickets-empty">
          <div className="tickets-empty-icon">⚠️</div>
          <h3 className="tickets-empty-title">{error || 'Обращение не найдено'}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container support-page">
      <PageHeader title={`Обращение #${ticket.id}`} />

      <div className="support-content">
        <div className="ticket-detail">
          {/* Ticket info */}
          <div className="ticket-info">
            <h2 className="ticket-info-subject">{ticket.subject}</h2>
            <div className="ticket-info-meta">
              <span className={`ticket-status ${ticket.status}`}>
                {ticket.status === 'open' ? 'Открыт' : 'Закрыт'}
              </span>
              <span>{formatMessageTime(ticket.created_at)}</span>
            </div>
          </div>

          {/* Messages */}
          <div className="messages-container">
            {ticket.messages?.map((message: SupportTicketMessage) => (
              <div
                key={message.id}
                className={`message ${message.is_admin ? 'admin' : 'user'}`}
              >
                <div className="message-sender">
                  {message.is_admin ? '👤 Поддержка' : '💬 Вы'}
                </div>
                <div className="message-text">{message.message}</div>
                <div className="message-time">{formatMessageTime(message.created_at)}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply form or closed notice */}
          {ticket.status === 'open' ? (
            <div className="reply-form">
              <textarea
                className="reply-input"
                placeholder="Написать сообщение..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending}
                rows={1}
              />
              <button
                className="reply-btn"
                onClick={handleSendMessage}
                disabled={isSending || !replyText.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22 2L11 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="closed-notice">
              <p>Это обращение закрыто. Если у вас есть дополнительные вопросы, создайте новое обращение.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

